import { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useStore } from 'react-redux';
import knowledgeServiceApi from 'shared/api/knowledge.service.api';
import { KNOWLEDGE_UPDATE_ITEM_CONTENT } from 'shared/store/actions';
import { KNOWLEDGE_TYPES } from 'shared/constants';

const MAX_CONCURRENT_REQUESTS = 3;
const DEBOUNCE_MS = 150;

export function usePreloadVisibleContent({ enabled }) {
  const dispatch = useDispatch();
  const store = useStore();

  const abortControllersRef = useRef(new Map());
  const observerRef = useRef(null);
  const visibleItemsRef = useRef(new Set());
  const pendingFetchesRef = useRef(new Set());
  const processTimeoutRef = useRef(null);
  const enabledRef = useRef(enabled);
  const fetchedItemsRef = useRef(new Set());
  const observedElementsRef = useRef(new Set());
  const pendingObservationsRef = useRef([]);

  useEffect(() => {
    enabledRef.current = enabled;
    if (!enabled) {
      fetchedItemsRef.current.clear();
    }
  }, [enabled]);

  const hasContent = useCallback(
    itemId => {
      const state = store.getState();
      const item = state.knowledge.itemsById?.[itemId];
      return !!item?.content;
    },
    [store],
  );

  const shouldSkipItem = useCallback(
    itemId => {
      const pendingFetches = pendingFetchesRef.current;
      const fetchedItems = fetchedItemsRef.current;
      return hasContent(itemId) || pendingFetches.has(itemId) || fetchedItems.has(itemId);
    },
    [hasContent],
  );

  const fetchItemContent = useCallback(
    async itemId => {
      if (shouldSkipItem(itemId)) {
        return;
      }

      const abortControllers = abortControllersRef.current;
      const pendingFetches = pendingFetchesRef.current;
      const fetchedItems = fetchedItemsRef.current;

      const abortController = new AbortController();
      abortControllers.set(itemId, abortController);
      pendingFetches.add(itemId);
      fetchedItems.add(itemId);

      try {
        const result = await knowledgeServiceApi.getItemContent({
          id: itemId,
          signal: abortController.signal,
        });

        if (!result.error && result.data && enabledRef.current) {
          dispatch({
            type: KNOWLEDGE_UPDATE_ITEM_CONTENT,
            payload: { item: result.data },
          });
        }
      } catch (error) {
        if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
          console.warn('Failed to preload content for item:', itemId, error);
          fetchedItems.delete(itemId);
        }
      } finally {
        pendingFetches.delete(itemId);
        abortControllers.delete(itemId);
      }
    },
    [shouldSkipItem, dispatch],
  );

  const processQueue = useCallback(() => {
    if (!enabledRef.current) return;

    const visibleItems = visibleItemsRef.current;
    const pendingFetches = pendingFetchesRef.current;

    const visibleIds = Array.from(visibleItems);
    const availableSlots = MAX_CONCURRENT_REQUESTS - pendingFetches.size;

    if (availableSlots <= 0) return;

    const toFetch = visibleIds.filter(id => !shouldSkipItem(id)).slice(0, availableSlots);

    toFetch.forEach(fetchItemContent);
  }, [shouldSkipItem, fetchItemContent]);

  const handleVisibilityChange = useCallback(
    entries => {
      const visibleItems = visibleItemsRef.current;

      entries.forEach(entry => {
        const itemId = entry.target.dataset.itemId;
        if (!itemId) return;

        if (entry.isIntersecting) {
          visibleItems.add(itemId);
        } else {
          visibleItems.delete(itemId);
        }
      });

      if (processTimeoutRef.current) {
        clearTimeout(processTimeoutRef.current);
      }
      processTimeoutRef.current = setTimeout(processQueue, DEBOUNCE_MS);
    },
    [processQueue],
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const abortControllers = abortControllersRef.current;
    const pendingFetches = pendingFetchesRef.current;
    const visibleItems = visibleItemsRef.current;
    const observedElements = observedElementsRef.current;
    const pendingObservations = pendingObservationsRef.current;

    observerRef.current = new IntersectionObserver(handleVisibilityChange, {
      rootMargin: '100px 0px',
      threshold: 0,
    });

    pendingObservations.forEach(({ node, elementKey }) => {
      if (!observedElements.has(elementKey) && node.isConnected) {
        observerRef.current.observe(node);
        observedElements.add(elementKey);
      }
    });
    pendingObservationsRef.current = [];

    return () => {
      abortControllers.forEach(controller => {
        controller.abort();
      });
      abortControllers.clear();
      pendingFetches.clear();
      visibleItems.clear();
      observedElements.clear();

      if (processTimeoutRef.current) {
        clearTimeout(processTimeoutRef.current);
        processTimeoutRef.current = null;
      }

      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [enabled, handleVisibilityChange]);

  const observeRef = useCallback((node, itemId, itemType) => {
    if (!node || !itemId) return;
    if (itemType !== KNOWLEDGE_TYPES.CONTENT) return;

    const observedElements = observedElementsRef.current;
    const elementKey = `${itemId}-${node.id || ''}`;

    if (observedElements.has(elementKey)) return;

    node.dataset.itemId = String(itemId);

    if (observerRef.current) {
      observerRef.current.observe(node);
      observedElements.add(elementKey);
    } else {
      pendingObservationsRef.current.push({ node, elementKey });
    }
  }, []);

  return { observeRef };
}
